import { navigateTo, useRoute } from '#imports'
import { type Ref, onMounted } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

/**
 * The product pages relies on the URL to know its current state, including the currently selected grade, which lies
 * in the URL hash (i.e.: #l=10).
 *
 * When the page is accessed without any hash, we add the grade based on the default selected grade for the product. This happens quite
 * often from linking in our app or by SEO access.
 */
export const useAddGradeInUrl = (
  selectedOffer: Ref<GetPickersResponse['selectedOffer'] | null | undefined>,
) => {
  const { grade } = useUrlParams()
  const route = useRoute()
  const logger = useLogger()

  // INC-2609: Grade URL changes MUST be client-only. An SSR redirect here
  // would poison Cloudflare's cache key and aim crawlers at robots.txt-blocked URLs.
  onMounted(async () => {
    if (grade.value || !selectedOffer.value) {
      return
    }

    logger.info('[PRODUCT] Add missing grade in URL', {
      owners: ['bot-squad-product-page-front'],
    })

    const link = {
      ...route,
      type: 'internal',
      query: {
        ...route.query,
        l: selectedOffer.value.grade.value.toString(),
      },
      hash: route.hash,
      replace: true,
    }

    await navigateTo(link)
  })
}
